import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { 
  ArrowLeft, Clock, Package, TrendingUp, Award, Sun, 
  Coffee, CheckCircle2, XCircle, ChevronRight, LogOut, Moon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Imagem de paisagem relaxante
const PEACEFUL_IMAGE = "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&auto=format&fit=crop&q=60";

export default function FimDia() {
  const { user: authUser, logout } = useAuth();
  const navigate = useNavigate();
  const { username } = useParams();
  const location = useLocation();
  
  // Detectar modo de simulação
  const isSimulation = location.pathname.includes('/sim/');
  const [user, setUser] = useState(isSimulation ? null : authUser);
  const [simulatedUser, setSimulatedUser] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [relatorio, setRelatorio] = useState(null);
  const [modoTestes, setModoTestes] = useState(false); // Esconder tempos/eficiências

  // Carregar configuração para verificar modo testes
  useEffect(() => {
    loadConfiguracao();
  }, []);

  const loadConfiguracao = async () => {
    try {
      const response = await axios.get(`${API}/configuracao`);
      setModoTestes(response.data.modo_testes_esconder_tempos || false);
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  };

  // Carregar operador simulado se necessário
  useEffect(() => {
    if (isSimulation && username) {
      loadSimulatedUser();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSimulation, username]);

  useEffect(() => {
    if (isSimulation) {
      setUser(simulatedUser);
    }
  }, [simulatedUser, isSimulation]);

  const loadSimulatedUser = async () => {
    try {
      const response = await axios.get(`${API}/operadores/username/${username}`);
      setSimulatedUser(response.data);
    } catch (error) {
      console.error('Erro ao carregar operador simulado:', error);
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadRelatorio();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const loadRelatorio = async () => {
    try {
      // Carrega resumo do dia com dados enriquecidos
      const res = await axios.get(`${API}/analytics/operador/${user.id}/resumo-dia`);
      setRelatorio(res.data);
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
      // Criar dados vazios se API falhar
      setRelatorio({
        tempo_total: 0,
        tempo_pausa: 0,
        pecas_produzidas: 0,
        eficiencia_media: 0,
        registos_count: 0,
        registos: []
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTempo = (segundos) => {
    if (!segundos) return '0h 0min';
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    return `${horas}h ${minutos}min`;
  };

  const getEficienciaColor = (eficiencia) => {
    if (eficiencia >= 100) return 'text-sage';
    if (eficiencia >= 85) return 'text-sand';
    return 'text-terracotta';
  };

  const getEficienciaBg = (eficiencia) => {
    if (eficiencia >= 100) return 'bg-sage-soft';
    if (eficiencia >= 85) return 'bg-sand-light';
    return 'bg-terracotta/10';
  };

  const getMotivationalMessage = (eficiencia) => {
    if (eficiencia >= 100) return { 
      emoji: '🌟', 
      title: 'Dia excelente!', 
      message: 'Superaste as expectativas. Parabéns pelo trabalho!' 
    };
    if (eficiencia >= 90) return { 
      emoji: '💪', 
      title: 'Muito bom!', 
      message: 'Ótimo desempenho hoje. Continua assim!' 
    };
    if (eficiencia >= 80) return { 
      emoji: '👍', 
      title: 'Bom trabalho!', 
      message: 'Um dia sólido. Amanhã será ainda melhor!' 
    };
    return { 
      emoji: '🌱', 
      title: 'Dia de aprendizagem', 
      message: 'Cada dia é uma oportunidade para crescer.' 
    };
  };

  const handleTerminarDia = () => {
    toast.success('Bom descanso! Até amanhã');
    
    if (isSimulation) {
      // Em modo simulação, voltar para o gestor
      navigate('/gestor');
    } else {
      // Em modo real, fazer logout
      logout();
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-sage border-t-transparent mx-auto mb-4"></div>
          <p className="text-stone-500">A preparar o seu relatório...</p>
        </div>
      </div>
    );
  }

  const motivacao = getMotivationalMessage(relatorio?.eficiencia_media || 0);

  return (
    <div className="min-h-screen bg-warm-bg">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-stone-100 sticky top-0 z-10">
        <div className="p-4 flex items-center gap-3 max-w-lg mx-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/operador')}
            className="rounded-full"
          >
            <ArrowLeft className="h-5 w-5 text-navy" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-heading font-bold text-navy">Resumo do Dia</h1>
            <p className="text-xs text-stone-500">
              {new Date().toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="p-4 max-w-lg mx-auto space-y-4">
        {/* Botão Sair - TOPO como tela de confirmação */}
        <div className="space-y-3">
          <Button
            className="w-full h-14 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 text-base font-semibold shadow-lg active:scale-[0.98] transition-transform"
            onClick={handleTerminarDia}
            data-testid="sair-button"
          >
            <LogOut className="h-5 w-5 mr-2" />
            Sair e Terminar o Dia
          </Button>
          
          {/* Voltar ao trabalho (modo simulação) */}
          {isSimulation && (
            <Button
              variant="ghost"
              className="w-full h-12 rounded-full text-stone-400 hover:text-navy hover:bg-navy/5"
              onClick={() => navigate(`/sim/operador/${username}`)}
            >
              Voltar ao Trabalho
            </Button>
          )}
          
          {/* Hora atual */}
          <div className="text-center">
            <p className="text-xs text-stone-400">Hora de saída</p>
            <p className="text-lg font-mono font-bold text-stone-600">
              {new Date().toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </div>

        {/* Motivational Card */}
        <Card className="p-6 bg-gradient-to-br from-navy to-navy-hover rounded-3xl shadow-float text-white text-center">
          <span className="text-5xl mb-4 block">{motivacao.emoji}</span>
          <h2 className="text-2xl font-heading font-bold mb-2">{motivacao.title}</h2>
          <p className="text-white/80">{motivacao.message}</p>
        </Card>

        {/* Stats Grid */}
        <div className={`grid ${modoTestes ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
          {/* Tempo de Trabalho */}
          <Card className="p-4 bg-white rounded-2xl shadow-soft border-0">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-sage-soft flex items-center justify-center">
                <Clock className="h-5 w-5 text-sage" />
              </div>
              <p className="text-xs text-stone-500 uppercase tracking-wider">Tempo</p>
            </div>
            <p className="text-2xl font-bold text-navy">
              {formatTempo(relatorio?.tempo_total)}
            </p>
            <p className="text-xs text-stone-400 mt-1">trabalhado hoje</p>
          </Card>

          {/* Eficiência Geral - ESCONDER se modo testes */}
          {!modoTestes && (
            <Card className={`p-4 rounded-2xl shadow-soft border-0 ${getEficienciaBg(relatorio?.eficiencia_media)}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-white/50 flex items-center justify-center">
                  <TrendingUp className={`h-5 w-5 ${getEficienciaColor(relatorio?.eficiencia_media)}`} />
                </div>
                <p className="text-xs text-stone-500 uppercase tracking-wider">Eficiência</p>
              </div>
              <p className={`text-2xl font-bold ${getEficienciaColor(relatorio?.eficiencia_media)}`}>
                {relatorio?.eficiencia_media || 0}%
              </p>
              <p className="text-xs text-stone-400 mt-1">média geral</p>
            </Card>
          )}

          {/* Peças Produzidas */}
          <Card className="p-4 bg-white rounded-2xl shadow-soft border-0">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-sand-light flex items-center justify-center">
                <Package className="h-5 w-5 text-sand" />
              </div>
              <p className="text-xs text-stone-500 uppercase tracking-wider">Peças</p>
            </div>
            <p className="text-2xl font-bold text-navy">
              {relatorio?.pecas_produzidas || 0}
            </p>
            <p className="text-xs text-stone-400 mt-1">produzidas</p>
          </Card>

          {/* Tarefas Concluídas */}
          <Card className="p-4 bg-white rounded-2xl shadow-soft border-0">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-terracotta/10 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-terracotta" />
              </div>
              <p className="text-xs text-stone-500 uppercase tracking-wider">Tarefas</p>
            </div>
            <p className="text-2xl font-bold text-navy">
              {relatorio?.registos_count || 0}
            </p>
            <p className="text-xs text-stone-400 mt-1">concluídas</p>
          </Card>
        </div>

        {/* Tarefas do Dia */}
        {relatorio?.registos && relatorio.registos.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wider mb-3">
              Tarefas Realizadas
            </h3>
            <Card className="bg-white rounded-2xl shadow-soft border-0 overflow-hidden">
              {relatorio.registos.map((registo, index) => (
                <div 
                  key={registo.id || index}
                  className={`p-4 flex items-center justify-between ${
                    index > 0 ? 'border-t border-stone-100' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      !modoTestes && registo.eficiencia >= 90 ? 'bg-sage-soft' : 'bg-sand-light'
                    }`}>
                      {!modoTestes && registo.eficiencia >= 90 ? (
                        <CheckCircle2 className="h-4 w-4 text-sage" />
                      ) : (
                        <Clock className="h-4 w-4 text-sand" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-navy">{registo.of_numero || 'OF'}</p>
                      <p className="text-xs text-stone-500">{registo.operacao_nome || 'Operação'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-navy">{registo.quantidade_produzida || 0} un.</p>
                    {/* Eficiência por tarefa - ESCONDER se modo testes */}
                    {!modoTestes && (
                      <p className={`text-xs font-medium ${getEficienciaColor(registo.eficiencia)}`}>
                        {registo.eficiencia || 0}%
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </Card>
          </div>
        )}

        {/* Imagem de paz e descanso */}
        <div className="relative rounded-2xl overflow-hidden shadow-soft">
          <img 
            src={PEACEFUL_IMAGE} 
            alt="Praia ao pôr do sol" 
            className="w-full h-32 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-4">
            <p className="text-white text-sm italic">
              "Descanse bem. Amanhã é um novo dia cheio de oportunidades."
            </p>
          </div>
        </div>

        {/* Espaço final */}
        <div className="pb-8"></div>
      </div>
    </div>
  );
}
